import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RechargeCelebrationModal from "@/components/RechargeCelebrationModal";

/**
 * RechargeCelebrationHost — global poller that watches for unread
 * `recharge_approved` notifications and pops the celebration modal.
 *
 * Mounted once inside AppLayout so the celebration fires anywhere the
 * user happens to be inside the app when admin approves their recharge.
 *
 * Polling strategy (kept cheap):
 *   - Every 25s while the tab is focused
 *   - Immediately on window focus (return-to-tab)
 *   - Skipped entirely if user is not logged in
 */
const POLL_INTERVAL_MS = 25_000;

export default function RechargeCelebrationHost() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState(null);   // { id, data, ... }
  const [open, setOpen] = useState(false);
  const shownRef = useRef(new Set());              // notification ids already shown this session
  const inFlightRef = useRef(false);

  const poll = async () => {
    if (!user || inFlightRef.current) return;
    if (document.visibilityState === "hidden") return;
    inFlightRef.current = true;
    try {
      const r = await api.get("/notifications", { params: { unread_only: true, limit: 5 } });
      const rows = r.data?.notifications || [];
      // Find the most recent recharge_approved that we haven't shown yet.
      const next = rows.find((n) => n.kind === "recharge_approved" && !shownRef.current.has(n.id));
      if (next) {
        shownRef.current.add(next.id);
        setCurrent(next);
        setOpen(true);
      }
    } catch {
      // Silently ignore — will retry on next tick.
    } finally {
      inFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!user) return;
    // First poll immediately, then on interval + on tab focus
    poll();
    const t = setInterval(poll, POLL_INTERVAL_MS);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user?.id]);

  const handleClose = () => {
    setOpen(false);
    setCurrent(null);
  };

  const handleWalletChange = () => {
    // Refresh AuthContext user (wallet is not part of user, but this signals to
    // any Wallet page listening via focus/route to re-fetch on next mount).
    refresh && refresh();
    // Dispatch a lightweight event that pages (Wallet, Header balance chip) can
    // subscribe to for instant balance refresh without prop drilling.
    window.dispatchEvent(new CustomEvent("shakti:wallet-updated"));
  };

  if (!user) return null;

  return (
    <RechargeCelebrationModal
      open={open && !!current}
      notification={current}
      onClose={handleClose}
      onWalletChange={handleWalletChange}
    />
  );
}
